/**
 * Status Builder Section
 * 
 * Configure status categories for mapping workflow.
 * Updated with unified design tokens.
 */

import React from 'react';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TableBody,
  TableCell,
  TextField,
  useTheme,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { alpha, type Theme } from '@mui/material/styles';
import {
  useAddStatusCategory,
  useRemoveStatusCategory,
  useStatusBuilderConfiguration,
  useUpdateStatusCategory,
} from '../../../../shared/stores/useConfigurationStore';
import ActionHelpTooltip from '../../../../shared/ui/common/ActionHelpTooltip';
import { CaptionText } from '../../../../shared/ui/typography';
import type { StatusCategory } from '../../../../shared/types';
import { mappingOverviewTooltips } from '../../../../shared/config/mappingHelpCatalog';
import { WorkspaceSection } from '../../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import {
  CompactAddAction,
  MappingEditorHeader,
  MappingRow,
  ScopeEditorTableVariant,
  getScopeEditorActionButtonSx,
  getScopeEditorSelectSx,
  getScopeEditorTextFieldSx,
} from '../../../../shared/ui/mapping/ScopeEditorPrimitives';
type StatusPaletteKey = 'success' | 'error' | 'warning' | 'info' | 'secondary';

const COLOR_OPTIONS: Array<{ value: StatusPaletteKey; label: string }> = [
  { value: 'success', label: 'Green' },
  { value: 'error', label: 'Red' },
  { value: 'warning', label: 'Orange' },
  { value: 'info', label: 'Blue' },
  { value: 'secondary', label: 'Purple' },
];

const STATUS_PALETTE_KEYS: Record<StatusPaletteKey, true> = {
  success: true,
  error: true,
  warning: true,
  info: true,
  secondary: true,
};

const COLUMN_WIDTHS = {
  color: '18%',
  name: '24%',
  description: '48%',
  actions: '10%',
};

const isStatusPaletteKey = (value: string): value is StatusPaletteKey =>
  Object.prototype.hasOwnProperty.call(STATUS_PALETTE_KEYS, value);

const resolveStatusColor = (theme: Theme, value: string): string => {
  const normalizedValue = value.trim();

  if (isStatusPaletteKey(normalizedValue)) {
    return theme.palette[normalizedValue].main;
  }

  return normalizedValue || theme.palette.info.main;
};

const getSwatchRing = (theme: Theme, color: string): string => {
  try {
    return alpha(color, 0.22);
  } catch {
    return alpha(theme.palette.divider, 0.5);
  }
};

const getStatusColorLabel = (value: string): string =>
  COLOR_OPTIONS.find((option) => option.value === value)?.label ?? value;

const hasStatusColorOption = (value: string): boolean =>
  COLOR_OPTIONS.some((option) => option.value === value);

type StatusColorOptionLabelProps = {
  theme: Theme;
  value: string;
  label: string;
};

const StatusColorOptionLabel: React.FC<StatusColorOptionLabelProps> = ({ theme, value, label }) => {
  const color = resolveStatusColor(theme, value);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Box
        aria-hidden="true"
        data-testid="status-color-swatch"
        data-status-color={value}
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: color,
          boxShadow: `0 0 0 1px ${getSwatchRing(theme, color)}`,
        }}
      />
      <CaptionText>
        {label}
      </CaptionText>
    </Box>
  );
};

const StatusBuilderSection: React.FC = () => {
  const theme = useTheme();
  const { categories } = useStatusBuilderConfiguration();
  const addStatusCategory = useAddStatusCategory();
  const updateStatusCategory = useUpdateStatusCategory();
  const removeStatusCategory = useRemoveStatusCategory();

  const generateId = (name: string): string =>
    `status-${name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;

  const handleAddCategory = () => {
    const baseName = 'New category';
    const duplicateCount = categories.filter((category) =>
      category.name.startsWith(baseName)
    ).length;
    const nextName = duplicateCount === 0 ? baseName : `${baseName} ${duplicateCount + 1}`;

    const newCategory: StatusCategory = {
      id: generateId(nextName),
      name: nextName,
      color: 'info',
      isBuiltIn: false,
      description: undefined,
      // New categories match Phase Created with their own name by default;
      // the user refines phase names / attributes in the category editor.
      phaseKind: 'Created',
      phaseNames: [nextName],
      attributes: [],
    };

    addStatusCategory(newCategory);
  };

  return (
    <WorkspaceSection
      title="Project phase status categories"
      titleHelp={mappingOverviewTooltips.statusBuilder}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '& > :last-child': {
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        },
      }}
    >
      <ScopeEditorTableVariant ariaLabel="Project phase status category editor" minWidth={560}>
        <MappingEditorHeader
          columns={[
            { key: 'color', width: COLUMN_WIDTHS.color, content: 'Status color' },
            { key: 'name', width: COLUMN_WIDTHS.name, content: 'Category name' },
              { key: 'description', width: COLUMN_WIDTHS.description, content: 'Downstream meaning' },
            { key: 'actions', width: COLUMN_WIDTHS.actions, align: 'center', content: 'Category actions' },
          ]}
        />
        <TableBody>
          {categories.map((category) => (
            <MappingRow key={category.id}>
              <TableCell sx={{ width: COLUMN_WIDTHS.color }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={category.color}
                    onChange={(event) =>
                      updateStatusCategory(category.id, { color: event.target.value })
                    }
                    sx={getScopeEditorSelectSx(theme)}
                    inputProps={{ 'aria-label': `Status color for ${category.name}` }}
                    renderValue={(selected) => {
                      const selectedValue = String(selected);

                      return (
                        <StatusColorOptionLabel
                          theme={theme}
                          value={selectedValue}
                          label={getStatusColorLabel(selectedValue)}
                        />
                      );
                    }}
                  >
                    {!hasStatusColorOption(category.color) && category.color.trim() ? (
                      <MenuItem value={category.color}>
                        <StatusColorOptionLabel
                          theme={theme}
                          value={category.color}
                          label={category.color}
                        />
                      </MenuItem>
                    ) : null}
                    {COLOR_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        <StatusColorOptionLabel
                          theme={theme}
                          value={option.value}
                          label={option.label}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.name }}>
                <TextField
                  size="small"
                  value={category.name}
                  onChange={(event) =>
                    updateStatusCategory(category.id, { name: event.target.value })
                  }
                  disabled={category.isBuiltIn}
                  fullWidth
                  variant="outlined"
                  sx={getScopeEditorTextFieldSx(theme)}
                  inputProps={{ 'aria-label': `Status category name for ${category.name}` }}
                />
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.description }}>
                <TextField
                  size="small"
                  value={category.description || ''}
                  onChange={(event) =>
                    updateStatusCategory(category.id, {
                      description: event.target.value || undefined,
                    })
                  }
                  fullWidth
                  variant="outlined"
                           placeholder="Describe what this status means downstream…"
                  sx={getScopeEditorTextFieldSx(theme)}
                  inputProps={{ 'aria-label': `Downstream meaning for ${category.name}` }}
                />
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.actions, textAlign: 'center' }}>
                {!category.isBuiltIn ? (
                  <ActionHelpTooltip help={mappingOverviewTooltips.deleteStatusCategory}>
                    <IconButton
                      size="small"
                      onClick={() => removeStatusCategory(category.id)}
                      sx={getScopeEditorActionButtonSx(theme, 'danger')}
                      aria-label={`Delete ${category.name}`}
                    >
                      <DeleteIcon sx={{ fontSize: '0.6875rem' }} />
                    </IconButton>
                  </ActionHelpTooltip>
                ) : (
                  <ActionHelpTooltip
                    help={mappingOverviewTooltips.deleteStatusCategoryDisabled}
                    disabled
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled
                        sx={getScopeEditorActionButtonSx(theme)}
                        aria-label={`${category.name} is a built-in status category and cannot be deleted`}
                      >
                        <DeleteIcon sx={{ fontSize: '0.6875rem' }} />
                      </IconButton>
                    </span>
                  </ActionHelpTooltip>
                )}
              </TableCell>
            </MappingRow>
          ))}
          <MappingRow>
            <TableCell colSpan={4} sx={{ py: 0.5 }}>
              <ActionHelpTooltip help={mappingOverviewTooltips.addStatusCategory}>
              <CompactAddAction onClick={handleAddCategory}>Add status category</CompactAddAction>
              </ActionHelpTooltip>
            </TableCell>
          </MappingRow>
        </TableBody>
      </ScopeEditorTableVariant>
    </WorkspaceSection>
  );
};

export default StatusBuilderSection;
